import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Users, BookOpen, GraduationCap, DollarSign, 
  Award, Ticket, MessageSquare, TrendingUp,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    total_students: 0,
    total_courses: 0,
    total_enrollments: 0,
    total_faculty: 0,
    pending_scholarships: 0,
    open_tickets: 0,
    new_inquiries: 0,
    total_revenue: 0,
    category_enrollments: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const statCards = [
    { title: 'Total Students', value: stats.total_students, icon: Users, color: 'bg-primary', link: '/admin/students' },
    { title: 'Active Courses', value: stats.total_courses, icon: BookOpen, color: 'bg-green-500', link: '/admin/courses' },
    { title: 'Total Enrollments', value: stats.total_enrollments, icon: GraduationCap, color: 'bg-purple-500', link: '/admin/enrollments' },
    { title: 'Revenue', value: `₹${(stats.total_revenue / 1000).toFixed(1)}K`, icon: DollarSign, color: 'bg-yellow-500', link: '/admin/enrollments' },
    { title: 'Pending Scholarships', value: stats.pending_scholarships, icon: Award, color: 'bg-orange-500', link: '/admin/scholarships' },
    { title: 'Open Tickets', value: stats.open_tickets, icon: Ticket, color: 'bg-red-500', link: '/admin/tickets' },
    { title: 'New Inquiries', value: stats.new_inquiries, icon: MessageSquare, color: 'bg-indigo-500', link: '/admin/inquiries' },
    { title: 'Faculty Members', value: stats.total_faculty, icon: Users, color: 'bg-teal-500', link: '/admin/faculty' },
  ];

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <Link to={stat.link} key={stat.title}>
            <Card className="card-hover" data-testid={`stat-${index}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" className="w-full justify-between" asChild>
              <Link to="/admin/courses">
                Add New Course <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" className="w-full justify-between" asChild>
              <Link to="/admin/faculty">
                Add Faculty <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" className="w-full justify-between" asChild>
              <Link to="/admin/notices">
                Publish Notice <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" className="w-full justify-between" asChild>
              <Link to="/admin/toppers">
                Add Topper <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Enrollments by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(stats.category_enrollments).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.category_enrollments).map(([category, count]) => (
                  <div key={category} className="flex justify-between items-center">
                    <span className="text-sm">{category}</span>
                    <span className="font-bold">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">No enrollment data yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pending Tasks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.pending_scholarships > 0 && (
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span className="text-sm">Scholarship Applications</span>
                <span className="font-bold text-orange-600">{stats.pending_scholarships}</span>
              </div>
            )}
            {stats.open_tickets > 0 && (
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="text-sm">Open Support Tickets</span>
                <span className="font-bold text-red-600">{stats.open_tickets}</span>
              </div>
            )}
            {stats.new_inquiries > 0 && (
              <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
                <span className="text-sm">New Inquiries</span>
                <span className="font-bold text-primary">{stats.new_inquiries}</span>
              </div>
            )}
            {stats.pending_scholarships === 0 && stats.open_tickets === 0 && stats.new_inquiries === 0 && (
              <p className="text-muted-foreground text-sm text-center py-4">All caught up!</p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
